import {
  Input,
  InputAdornment,
  TableContainer,
  TableHead,
  Table,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  TablePagination,
} from '@material-ui/core';
import SearchOutlined from '@material-ui/icons/SearchOutlined';
import { Alert } from '@material-ui/lab';
import React, { useMemo, useState } from 'react';
import Truncate from 'v2/components/Shared/TruncateText';
import { formatMoney } from 'v2/helpers/money';

import type { PlanChangeConfig } from 'v2/interfaces/product';

import { useStyles } from './styles';

const PER_PAGE = 10;

function TableState({ message }: { message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={5} align='center' style={{ padding: 16 }}>
        {message}
      </TableCell>
    </TableRow>
  );
}

type ProductsListProps = {
  data: PlanChangeConfig;
  displayProduct?: boolean;
  handleSelectPlan: (planId: number) => void;
  handleSelectAllPlans: () => void;
};

function ProductsList({ data, handleSelectPlan, handleSelectAllPlans, displayProduct = false }: ProductsListProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const classes = useStyles();

  const selectedPlansCount = useMemo(() => data?.filter((plan) => plan.selected)?.length || 0, [data]);

  const paginatedPlans = useMemo(() => {
    const init = page * PER_PAGE;
    const end = init + PER_PAGE;
    return data.slice(init, end);
  }, [data, page]);

  const filteredPlans = useMemo(
    () =>
      (paginatedPlans || [])?.filter((plan) => {
        const titleMatches = plan.plan_title.toLowerCase().includes(search.toLowerCase());
        const idMatches = plan.product_id.toString().includes(search);
        return titleMatches || idMatches;
      }),
    [paginatedPlans, search]
  );

  const isIndeterminate = selectedPlansCount > 0 && selectedPlansCount < data?.length;

  return (
    <div className={classes.container}>
      <Input
        className='change-plan-search-input'
        fullWidth
        placeholder='Procurar por nome ou ID do produto'
        startAdornment={
          <InputAdornment position='start'>
            <SearchOutlined fontSize='small' />
          </InputAdornment>
        }
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <p>Selecione abaixo o(s) plano(s) que você possibilitará que seu cliente migre:</p>
      <TableContainer style={{ maxHeight: 220 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} padding='checkbox'>
                <Checkbox
                  className='change-plan-select-all'
                  checked={selectedPlansCount === data?.length && data?.length > 0}
                  indeterminate={isIndeterminate}
                  onChange={() => handleSelectAllPlans()}
                />
              </TableCell>
              <TableCell>Nome do plano</TableCell>
              {displayProduct && <TableCell>Produto</TableCell>}
              <TableCell>Descrição</TableCell>
              <TableCell width={150} align='right'>
                Preço
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!filteredPlans?.length && <TableState message='Nenhum plano encontrado' />}
            {filteredPlans.map((plan) => (
              <TableRow key={plan.plan_id}>
                <TableCell padding='checkbox'>
                  <Checkbox id={`change-plan-select-${plan.plan_id}`} checked={plan.selected} onChange={() => handleSelectPlan(plan.plan_id)} />
                </TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  <Truncate count={20}>{plan.plan_title}</Truncate>
                </TableCell>
                {displayProduct && (
                  <TableCell>
                    <Truncate count={20}>{plan.product_name}</Truncate>
                  </TableCell>
                )}
                <TableCell>
                  <Truncate count={20}>{plan.plan_desc}</Truncate>
                </TableCell>
                <TableCell align='right'>{formatMoney(plan.base_price)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          {data?.length > 0 && (
            <TablePagination
              rowsPerPage={PER_PAGE}
              rowsPerPageOptions={[]}
              count={data?.length || 0}
              page={page}
              onPageChange={(_, page) => setPage(page)}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          )}
        </Table>
      </TableContainer>
      {selectedPlansCount === 0 && <Alert severity='warning'>Você precisa selecionar pelo menos um plano</Alert>}
    </div>
  );
}

export { ProductsList };
